import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { GitCompare, Loader2, AlertCircle, FileDown, Star } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

function CmpMarkdown({ text }) {
  const lines = (text || "").split("\n"); const out = []; let b = [];
  const flush = (i) => { if (b.length) { out.push(<ul key={`u${i}`} className="my-2 ml-4 list-disc space-y-1">{b}</ul>); b = []; } };
  const inl = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") && p.endsWith("**") ? <strong key={j} className="text-navy">{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>);
  lines.forEach((ln, i) => {
    const t = ln.trim();
    if (!t || t === "---" || t === "***" || /^\|[-\s|]+\|$/.test(t)) { flush(i); return; }
    if (t.startsWith("### ")) { flush(i); out.push(<h5 key={i} className="mt-2 mb-1 font-semibold text-navy">{inl(t.slice(4))}</h5>); }
    else if (t.startsWith("## ")) { flush(i); out.push(<h4 key={i} className="mt-3 mb-1 font-display text-sm font-semibold text-navy">{inl(t.slice(3))}</h4>); }
    else if (t.startsWith("# ")) { flush(i); out.push(<h4 key={i} className="mt-2 mb-1 font-display text-base font-bold text-navy">{inl(t.slice(2))}</h4>); }
    else if (t.startsWith("- ") || t.startsWith("* ")) b.push(<li key={i} className="text-sm text-slate-600">{inl(t.slice(2))}</li>);
    else { flush(i); out.push(<p key={i} className="my-1 text-sm text-slate-600">{inl(t)}</p>); }
  });
  flush("end"); return <div>{out}</div>;
}

const MODEL_STYLE = {
  claude: { border: "border-amber-200", head: "bg-amber-50 text-amber-800", dot: "bg-amber-500" },
  gemini: { border: "border-sky-200", head: "bg-sky-50 text-sky-800", dot: "bg-sky-500" },
};

const stripMd = (s) => (s || "").split("\n").map((l) => {
  let t = l.trim();
  if (!t || t === "---" || t === "***") return "";
  t = t.replace(/^#{1,6}\s*/, "").replace(/\*\*/g, "");
  if (t.startsWith("- ") || t.startsWith("* ")) t = "• " + t.slice(2);
  return t;
});

function ModelColumn({ slotKey, slot, preferred, onPrefer, allDone }) {
  const st = MODEL_STYLE[slotKey] || MODEL_STYLE.claude;
  const isPref = preferred === slotKey;
  return (
    <div className={`flex flex-col rounded-xl border ${isPref ? "border-emerald-400 ring-1 ring-emerald-400" : st.border} bg-white`} data-testid={`ai-compare-col-${slotKey}`}>
      <div className={`flex items-center gap-2 rounded-t-xl px-4 py-2.5 ${st.head}`}>
        <span className={`h-2.5 w-2.5 rounded-full ${st.dot}`} />
        <span className="text-sm font-semibold">{slot?.label || slotKey}</span>
        {isPref && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700" data-testid={`ai-compare-preferred-${slotKey}`}>Préféré</span>}
        {allDone && slot?.status === "done" && (
          <button type="button" onClick={() => onPrefer(isPref ? "" : slotKey)} data-testid={`ai-compare-prefer-${slotKey}`}
            className={`ml-auto flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${isPref ? "bg-emerald-600 text-white" : "bg-white/60 text-slate-600 hover:bg-white"}`}>
            <Star size={11} className={isPref ? "fill-white" : ""} /> {isPref ? "Choisi" : "Préférer"}
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: "52vh" }}>
        {(!slot || slot.status === "running") && (
          <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Génération en cours…</div>
        )}
        {slot?.status === "error" && (
          <div className="flex items-start gap-2 text-sm text-red-600"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {slot.error || "Échec de la génération."}</div>
        )}
        {slot?.status === "done" && <CmpMarkdown text={slot.analysis} />}
      </div>
    </div>
  );
}

export function AiCompare({ moduleKey, params = {}, className = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const timer = useRef(null);

  const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };

  const poll = async () => {
    try {
      const { data: d } = await api.get(`/admin/ai-compare?module_key=${encodeURIComponent(moduleKey)}`);
      setData(d);
      if (d.status !== "running") { stop(); setBusy(false); }
    } catch (e) { stop(); setBusy(false); toast.error(apiError(e)); }
  };

  const run = async () => {
    setBusy(true); setData(null);
    try {
      await api.post("/admin/ai-compare", { module_key: moduleKey, params });
      timer.current = setInterval(poll, 4000);
      poll();
    } catch (e) { setBusy(false); toast.error(apiError(e, "Comparaison impossible")); }
  };

  useEffect(() => () => stop(), []);

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) run(); else { stop(); setBusy(false); }
  };

  const prefer = async (winner) => {
    try {
      const { data: r } = await api.put("/admin/ai-compare/winner", { module_key: moduleKey, winner });
      setData((d) => ({ ...d, preferred: r.preferred }));
      toast.success(winner ? `Modèle préféré : ${r.label}` : "Préférence retirée");
    } catch (e) { toast.error(apiError(e)); }
  };

  const bothDone = data?.claude?.status === "done" && data?.gemini?.status === "done";

  const exportPdf = async () => {
    if (!bothDone) return;
    try {
      const { jsPDF } = await import("jspdf");
      const pdf = new jsPDF("l", "mm", "a4");
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 12, gap = 8, top = 26, lh = 4.6, bottom = 12;
      const colW = (pw - margin * 2 - gap) / 2;
      const x1 = margin, x2 = margin + colW + gap;
      const cl = stripMd(data.claude.analysis).flatMap((t) => t === "" ? [""] : pdf.splitTextToSize(t, colW - 2));
      const ge = stripMd(data.gemini.analysis).flatMap((t) => t === "" ? [""] : pdf.splitTextToSize(t, colW - 2));
      const rows = Math.max(cl.length, ge.length);
      const drawHeader = () => {
        pdf.setFontSize(13); pdf.setTextColor(10, 37, 64);
        pdf.text("Comparateur IA — Claude vs Gemini", margin, 14);
        pdf.setFontSize(8); pdf.setTextColor(120, 130, 145);
        pdf.text(data.label || "", pw - margin, 14, { align: "right" });
        pdf.setFontSize(10);
        pdf.setTextColor(146, 64, 14); pdf.text("Claude Sonnet 4.6", x1, 22);
        pdf.setTextColor(7, 89, 133); pdf.text("Gemini 3.1 Pro", x2, 22);
        pdf.setDrawColor(226, 232, 240); pdf.line(margin, 23.5, pw - margin, 23.5);
        pdf.setFontSize(8.5); pdf.setTextColor(51, 65, 85);
      };
      drawHeader();
      let y = top;
      for (let i = 0; i < rows; i++) {
        if (y > ph - bottom) { pdf.addPage(); drawHeader(); y = top; }
        if (cl[i]) pdf.text(cl[i], x1, y);
        if (ge[i]) pdf.text(ge[i], x2, y);
        y += lh;
      }
      pdf.save(`comparaison-ia-${moduleKey.replace(/[:]/g, "-")}.pdf`);
      toast.success("PDF généré");
    } catch { toast.error("Export PDF impossible"); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => onOpenChange(true)} data-testid={`ai-compare-btn-${moduleKey}`}
        className={`rounded-full border-slate-300 text-xs ${className}`}>
        <GitCompare size={13} className="mr-1" /> Comparer les 2 modèles
      </Button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl" data-testid="ai-compare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-navy">
              <GitCompare size={18} className="text-ciel" /> Comparateur IA — Claude vs Gemini
              {data?.label && <span className="text-sm font-normal text-slate-400">· {data.label}</span>}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Les deux modèles analysent exactement les mêmes données. La génération prend ~30 à 70 s.
          </p>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <ModelColumn slotKey="claude" slot={data?.claude} preferred={data?.preferred} onPrefer={prefer} allDone={bothDone} />
            <ModelColumn slotKey="gemini" slot={data?.gemini} preferred={data?.preferred} onPrefer={prefer} allDone={bothDone} />
          </div>
          <div className="mt-2 flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="outline" onClick={exportPdf} disabled={!bothDone} data-testid="ai-compare-pdf" className="rounded-full text-xs">
              <FileDown size={13} className="mr-1" /> Télécharger en PDF
            </Button>
            <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="ai-compare-rerun" className="rounded-full text-xs">
              {busy ? <><Loader2 size={13} className="mr-1 animate-spin" /> En cours…</> : "Relancer la comparaison"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
